import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import attendanceAPI from '../api/attendanceAPI';
import { useAttendanceAuth } from '../context/AttendanceAuthContext';
import { 
  MdArrowBack, 
  MdSave,
  MdAdd,
  MdDelete
} from 'react-icons/md';
import SubjectScheduleMatrix from './SubjectScheduleMatrix';
import AddSubjectModal from './AddSubjectModal';
import SharedClassBadge from './SharedClassBadge';

/**
 * Edit Timetable Component
 * Form to edit an existing timetable (only if state is 'draft')
 */
function EditTimetable() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getToken } = useAttendanceAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Form data
  const [formData, setFormData] = useState({
    faculty_id: '',
    department_id: '',
    department_name: '',
    campus: '',
    batch_ids: [],
    semester: '',
    shift_ids: [],
    start_date: '',
    end_date: '',
    subjects: []
  });

  // Options data
  const [departments, setDepartments] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [batches, setBatches] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [weekdays, setWeekdays] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [showAddSubjectModal, setShowAddSubjectModal] = useState(false);
  const [timetableState, setTimetableState] = useState(null);
  const [activeTermConfig, setActiveTermConfig] = useState(null);
  const autoPopulateKeyRef = useRef('');

  useEffect(() => {
    // Validate timetable ID first
    if (!id || id === 'undefined') {
      setError('Invalid timetable ID');
      setLoading(false);
      return;
    }
    
    const timetableId = parseInt(id);
    if (isNaN(timetableId) || timetableId <= 0) {
      setError('Invalid timetable ID format');
      setLoading(false);
      return;
    }
    
    loadOptions();
    loadTimetable();
  }, [id]);

  useEffect(() => {
    // Reload whenever the selected shifts change so newly-added shifts appear.
    if (formData.shift_ids.length > 0) {
      loadAllPeriods();
    } else if (formData.shift_ids.length === 0) {
      setPeriods([]);
    }
  }, [formData.shift_ids]);

  useEffect(() => {
    if (!formData.faculty_id) {
      setBatches([]);
      return;
    }
    attendanceAPI.getBatches({
      department_id: formData.department_id || undefined,
      campus: formData.campus || undefined,
      faculty_id: formData.faculty_id || undefined
    }, getToken())
      .then(result => setBatches(result.success ? (result.batches || []) : []))
      .catch(err => {
        console.error('Error loading campus batches:', err);
        setBatches([]);
      });
  }, [formData.campus, formData.department_id, formData.faculty_id]);

  useEffect(() => {
    if (formData.campus && formData.batch_ids.length > 0 && formData.semester) {
      loadSubjects();
    }
  }, [formData.campus, formData.batch_ids, formData.semester]);
  useEffect(() => {
    const ready = Boolean(formData.campus)
      && formData.batch_ids.length > 0
      && Boolean(formData.semester)
      && formData.shift_ids.length > 0
      && periods.length > 0;
    if (!ready) return;

    const selectionKey = [
      formData.campus,
      [...formData.batch_ids].sort((a, b) => a - b).join(','),
      formData.semester,
      [...formData.shift_ids].sort((a, b) => a - b).join(','),
    ].join('|');
    if (autoPopulateKeyRef.current === selectionKey) return;

    autoPopulateKeyRef.current = selectionKey;
    handleAutoPopulate(selectionKey);
  }, [formData.campus, formData.batch_ids, formData.semester, formData.shift_ids, periods.length]);

  // Format subjects schedule data when weekdays are loaded
  useEffect(() => {
    if (weekdays.length > 0 && formData.subjects.length > 0) {
      const formattedSubjects = formData.subjects.map(subject => {
        // Check if subject already has period_X_days format
        const hasPeriodDays = Object.keys(subject).some(key => key.startsWith('period_') && key.endsWith('_days'));
        if (hasPeriodDays) {
          return subject; // Already formatted
        }
        
        // Format from schedule object
        const formatted = { ...subject };
        if (subject.schedule) {
          Object.keys(subject.schedule).forEach(key => {
            if (key.includes('period_') && key.includes('_ids')) {
              // Already in correct format (period_1_ids)
              const periodNum = key.match(/period_(\d+)_ids/)?.[1];
              if (periodNum) {
                formatted[`period_${periodNum}_days`] = subject.schedule[key] || [];
              }
            } else if (key.includes('period_') && !key.includes('_ids')) {
              // Convert period_1: ["Saturday", "Sunday"] to period_1_days: [1, 2]
              const periodNum = key.match(/period_(\d+)/)?.[1];
              if (periodNum && Array.isArray(subject.schedule[key])) {
                const dayNames = subject.schedule[key];
                const dayIds = dayNames.map(dayName => {
                  const day = weekdays.find(d => d.name === dayName);
                  return day ? day.id : null;
                }).filter(id => id !== null);
                formatted[`period_${periodNum}_days`] = dayIds;
              }
            }
          });
        }
        return formatted;
      });
      
      // Only update if formatting changed something
      const needsUpdate = formattedSubjects.some((subj, idx) => {
        const original = formData.subjects[idx];
        return JSON.stringify(subj) !== JSON.stringify(original);
      });
      
      if (needsUpdate) {
        setFormData(prev => ({
          ...prev,
          subjects: formattedSubjects
        }));
      }
    }
  }, [weekdays]);

  const loadTimetable = async () => {
    try {
      setLoading(true);
      
      // Validate timetable ID before API call
      if (!id || id === 'undefined') {
        setError('Timetable ID is required');
        return;
      }
      
      const timetableId = parseInt(id);
      if (isNaN(timetableId) || timetableId <= 0) {
        setError('Invalid timetable ID format');
        return;
      }
      
      // Make sure weekdays are loaded first (needed for conversion)
      if (weekdays.length === 0) {
        const token = getToken();
        const weekdaysRes = await attendanceAPI.getWeekdays(token);
        if (weekdaysRes.success) {
          setWeekdays(weekdaysRes.weekdays || []);
        }
      }
      
      const token = getToken();
      const result = await attendanceAPI.getTimetable(timetableId, token);
      
      if (result.success && result.timetable) {
        const timetable = result.timetable;
        setTimetableState(timetable.state);
        
        // Check if timetable can be edited (draft and active can be edited, ended cannot)
        if (timetable.state === 'ended') {
          setError('Cannot edit ended timetables');
          return;
        }

        // Set periods from timetable data
        if (timetable.periods && Array.isArray(timetable.periods)) {
          // Sort periods by period number for consistent ordering
          const sortedPeriods = [...timetable.periods].sort((a, b) => {
            const matchA = a.name?.match(/period\s*(\d+)/i);
            const matchB = b.name?.match(/period\s*(\d+)/i);
            const numA = matchA ? parseInt(matchA[1]) : 999;
            const numB = matchB ? parseInt(matchB[1]) : 999;
            return numA - numB;
          });
          setPeriods(sortedPeriods);
        }

        // Populate form with existing data
        // Handle both subject_lines (from API) and subjects (legacy) formats
        const subjectLines = timetable.subject_lines || timetable.subjects || [];
        
        // Sort periods for consistent mapping
        const sortedPeriods = timetable.periods ? [...timetable.periods].sort((a, b) => {
          const matchA = a.name?.match(/period\s*(\d+)/i);
          const matchB = b.name?.match(/period\s*(\d+)/i);
          const numA = matchA ? parseInt(matchA[1]) : 999;
          const numB = matchB ? parseInt(matchB[1]) : 999;
          return numA - numB;
        }) : [];
        
        // Convert schedule data from API format (period_{periodId}_days) to component format (period_{periodNum}_days)
        // The API uses period_{period_id}_days format, but SubjectScheduleMatrix uses period_1_days, period_2_days, etc.
        const formattedSubjects = subjectLines.map(subject => {
          const newSubject = { 
            id: subject.id, // Keep ID for updates
            subject_id: subject.subject_id || subject.id,
            subject_name: subject.subject_name || subject.name,
            tutor_id: subject.tutor_id || subject.employee_id || null,
            tutor_assignment_id: subject.tutor_assignment_id || null,
            tutor_name: subject.tutor_name || '',
            is_shared: Boolean(subject.is_shared)
          };
          
          // Convert from period_{periodId}_days (API format) to period_{periodNum}_days (component format)
          // where periodNum is the position in the sorted periods array (1, 2, 3...)
          sortedPeriods.forEach((period, periodIdx) => {
            const periodId = period.id;
            const periodNum = periodIdx + 1; // Component uses 1-based index
            const apiKey = `period_${periodId}_days`; // API format
            const componentKey = `period_${periodNum}_days`; // Component format
            
            // Check if subject has data in API format
            if (subject[apiKey] !== undefined) {
              newSubject[componentKey] = subject[apiKey] || [];
            } else {
              // Initialize empty array if not present
              newSubject[componentKey] = [];
            }
          });
          
          // Also handle legacy schedule format
          if (subject.schedule) {
            Object.keys(subject.schedule).forEach(key => {
              // If backend returns period_1_ids style
              if (key.includes('period_') && key.includes('_ids')) {
                const periodNum = key.match(/period_(\d+)_ids/)?.[1];
                if (periodNum) {
                  // Find period by sequence/number
                  const period = sortedPeriods.find(p => {
                    const match = p.name?.match(/period\s*(\d+)/i);
                    return match && parseInt(match[1]) === parseInt(periodNum);
                  });
                  if (period) {
                    const periodIdx = sortedPeriods.indexOf(period);
                    const componentKey = `period_${periodIdx + 1}_days`;
                    newSubject[componentKey] = subject.schedule[key] || [];
                  }
                }
              } else if (key.includes('period_') && !key.includes('_ids')) {
                // If backend returns day names e.g., period_1: ["Saturday"]
                const periodNum = key.match(/period_(\d+)/)?.[1];
                if (periodNum && Array.isArray(subject.schedule[key])) {
                  // Find period by sequence/number
                  const period = sortedPeriods.find(p => {
                    const match = p.name?.match(/period\s*(\d+)/i);
                    return match && parseInt(match[1]) === parseInt(periodNum);
                  });
                  if (period) {
                    const periodIdx = sortedPeriods.indexOf(period);
                    const componentKey = `period_${periodIdx + 1}_days`;
                    const dayIds = subject.schedule[key].map(dayName => {
                      const day = weekdays.find(d => d.name === dayName);
                      return day ? day.id : null;
                    }).filter(id => id !== null);
                    newSubject[componentKey] = dayIds;
                  }
                }
              }
            });
          }
          
          return newSubject;
        });

        try {
          const lookup = await attendanceAPI.getSubjects({
            batch_ids: timetable.batch_ids,
            semester: timetable.semester,
            campus: timetable.campus,
            faculty_id: timetable.faculty_id || undefined
          }, token);
          if (lookup.success) {
            const currentById = new Map((lookup.subjects || []).map(subject => [Number(subject.id), subject]));
            formattedSubjects.forEach(subject => {
              const current = currentById.get(Number(subject.subject_id));
              if (current) {
                subject.tutor_id = current.tutor_id || null;
                subject.tutor_assignment_id = current.tutor_assignment_id || null;
                subject.tutor_name = current.tutor_name || '';
                subject.is_shared = Boolean(current.is_shared);
              }
            });
          }
        } catch (lookupError) {
          console.warn('Could not refresh tutor assignments:', lookupError);
        }

        console.log('Loaded timetable subjects:', formattedSubjects);
        console.log('Periods:', sortedPeriods);

        autoPopulateKeyRef.current = [
          timetable.campus || '',
          [...(timetable.batch_ids || [])].sort((a, b) => a - b).join(','),
          timetable.semester || '',
          [...(timetable.shift_ids || [])].sort((a, b) => a - b).join(','),
        ].join('|');
        const [departmentsRes, batchesRes] = await Promise.all([
          attendanceAPI.getDepartments({
            faculty_id: timetable.faculty_id || undefined
          }, token),
          attendanceAPI.getBatches({
            department_id: timetable.department_id || undefined,
            campus: timetable.campus || undefined,
            faculty_id: timetable.faculty_id || undefined
          }, token)
        ]);
        if (departmentsRes.success) setDepartments(departmentsRes.departments || []);
        if (batchesRes.success) setBatches(batchesRes.batches || []);
        setFormData({
          faculty_id: timetable.faculty_id || '',
          department_id: timetable.department_id || '',
          department_name: timetable.department_name || '',
          campus: timetable.campus || '',
          batch_ids: timetable.batch_ids || [],
          semester: timetable.semester || '',
          shift_ids: timetable.shift_ids || [],
          start_date: timetable.start_date || '',
          end_date: timetable.end_date || '',
          subjects: formattedSubjects,
          exception_dates: timetable.exception_dates || []
        });
      } else {
        setError(result.message || 'Failed to load timetable');
      }
    } catch (err) {
      console.error('Error loading timetable:', err);
      setError(err.message || 'Failed to load timetable');
    } finally {
      setLoading(false);
    }
  };

  const loadOptions = async () => {
    const token = getToken();
    const errors = [];

    try {
      const campusesRes = await attendanceAPI.getCampuses(token);
      if (campusesRes.success) setCampuses(campusesRes.campuses || []);
      else errors.push('campuses');
    } catch (err) {
      console.error('Error loading campuses:', err);
      errors.push('campuses');
    }
    
    // Load each option individually to handle errors gracefully
    // Academic Years
    try {
      const yearsRes = await attendanceAPI.getAcademicYears(token);
      if (yearsRes.success) {
        setAcademicYears(yearsRes.academic_years || []);
      } else {
        console.warn('Failed to load academic years:', yearsRes.message);
        errors.push('academic years');
      }
    } catch (err) {
      console.warn('Error loading academic years:', err);
      errors.push('academic years');
    }
    
    // Shifts
    try {
      const shiftsRes = await attendanceAPI.getShifts(token);
      if (shiftsRes.success) {
        setShifts(shiftsRes.shifts || []);
      } else {
        console.warn('Failed to load shifts:', shiftsRes.message);
        errors.push('shifts');
      }
    } catch (err) {
      console.warn('Error loading shifts:', err);
      errors.push('shifts');
    }
    
    // Weekdays (critical for schedule editing)
    try {
      const weekdaysRes = await attendanceAPI.getWeekdays(token);
      if (weekdaysRes.success) {
        setWeekdays(weekdaysRes.weekdays || []);
      } else {
        console.error('Failed to load weekdays:', weekdaysRes.message);
        errors.push('weekdays');
      }
    } catch (err) {
      console.error('Error loading weekdays:', err);
      errors.push('weekdays');
    }
    
    // Term Config (optional)
    try {
      const termConfigRes = await attendanceAPI.getActiveTermConfig(token);
      if (termConfigRes.success) {
        setActiveTermConfig(termConfigRes);
      } else {
        console.warn('No active term config found:', termConfigRes.message);
        // Term config is optional, don't add to errors
      }
    } catch (err) {
      console.warn('Error loading term config (optional):', err);
      // Term config is optional, don't add to errors
    }
    
    // Only show error if critical options failed
    if (errors.length > 0) {
      const errorMessage = errors.length === 1 
        ? `Failed to load ${errors[0]}. Please refresh the page.`
        : `Failed to load: ${errors.join(', ')}. Please refresh the page.`;
      setError(errorMessage);
    }
  };

  /**
   * Extract period number from period name (e.g., "Period 1" -> 1, "Period 10" -> 10)
   * Used for sorting periods by period number, not by sequence or start_time
   */
  const extractPeriodNumber = (period) => {
    const match = period.name?.match(/period\s*(\d+)/i);
    return match ? parseInt(match[1]) : 999; // Fallback to 999 if no number found
  };

  const loadAllPeriods = async () => {
    try {
      const token = getToken();
      const allPeriods = [];
      
      // Load periods for all selected shifts
      for (const shiftId of formData.shift_ids) {
        const result = await attendanceAPI.getPeriods(shiftId, token);
        if (result.success && result.periods) {
          // Add shift info to each period
          // ⚠️ IMPORTANT: Periods from API are already sorted by period number
          // We just add shift info and combine them
          const periodsWithShift = result.periods.map(period => ({
            ...period,
            shift_id: shiftId,
            shift_name: shifts.find(s => s.id === shiftId)?.name || shifts.find(s => s.id === shiftId)?.code
          }));
          allPeriods.push(...periodsWithShift);
        }
      }
      
      // Keep each shift together. Sorting only by "Period 1/2/3" interleaves
      // morning and afternoon periods and breaks the positional schedule keys.
      const shiftOrder = new Map(
        formData.shift_ids.map((shiftId, index) => [Number(shiftId), index])
      );
      allPeriods.sort((a, b) => {
        const shiftDifference = (shiftOrder.get(Number(a.shift_id)) ?? 999)
          - (shiftOrder.get(Number(b.shift_id)) ?? 999);
        return shiftDifference || extractPeriodNumber(a) - extractPeriodNumber(b);
      });
      
      setPeriods(allPeriods);
    } catch (err) {
      console.error('Error loading periods:', err);
      setPeriods([]);
    }
  };

  const loadSubjects = async () => {
    try {
      const token = getToken();
      const result = await attendanceAPI.getSubjects({
        batch_ids: formData.batch_ids,
        semester: formData.semester,
        campus: formData.campus,
        faculty_id: formData.faculty_id || undefined
      }, token);
      if (result.success) {
        setSubjects(result.subjects || []);
      }
    } catch (err) {
      console.error('Error loading subjects:', err);
    }
  };

  const handleAutoPopulate = async (selectionKey) => {
    try {
      setLoading(true);
      const token = getToken();
      const result = await attendanceAPI.autoPopulateSubjects(
        formData.batch_ids,
        formData.semester,
        formData.shift_ids[0],
        formData.campus,
        formData.faculty_id,
        token
      );

      if (autoPopulateKeyRef.current !== selectionKey) return;
      if (result.success && result.subjects) {
        setFormData(prev => {
          const existingBySubjectId = new Map(
            prev.subjects.map(subject => [
              Number(subject.subject_id || subject.id),
              subject
            ])
          );
          const returnedIds = new Set();
          const subjectsWithSchedule = result.subjects.map(subject => {
            const subjectId = Number(subject.subject_id || subject.id);
            returnedIds.add(subjectId);
            const existing = existingBySubjectId.get(subjectId);
            const subjectWithSchedule = existing
              ? { ...existing, ...subject, id: existing.id }
              : { ...subject };

            periods.forEach((_, periodIdx) => {
              const key = `period_${periodIdx + 1}_days`;
              subjectWithSchedule[key] = existing && Array.isArray(existing[key])
                ? existing[key]
                : (Array.isArray(subjectWithSchedule[key]) ? subjectWithSchedule[key] : []);
            });
            return subjectWithSchedule;
          });

          // Preserve manual/existing timetable subjects not returned by lookup.
          prev.subjects.forEach(subject => {
            const subjectId = Number(subject.subject_id || subject.id);
            if (!returnedIds.has(subjectId)) {
              const preserved = { ...subject };
              periods.forEach((_, periodIdx) => {
                const key = `period_${periodIdx + 1}_days`;
                if (!Array.isArray(preserved[key])) preserved[key] = [];
              });
              subjectsWithSchedule.push(preserved);
            }
          });

          return { ...prev, subjects: subjectsWithSchedule };
        });
      } else {
        setError(result.message || 'Failed to load subjects automatically');
      }
    } catch (err) {
      if (autoPopulateKeyRef.current === selectionKey) {
        console.error('Error auto-populating subjects:', err);
        setError(err.message || 'Failed to load subjects automatically');
      }
    } finally {
      if (autoPopulateKeyRef.current === selectionKey) setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.campus) {
      setError('Campus is required');
      return;
    }
    if (!formData.batch_ids.length) {
      setError('Please select one batch');
      return;
    }
    if (!formData.semester) {
      setError('Please fill in all required fields');
      return;
    }
    if (!formData.start_date || !formData.end_date) {
      setError('Please select date range');
      return;
    }

    // Note: Removed validation that requires all subjects to have schedules
    // This allows partial updates - users can update other fields without being forced to assign schedules to all subjects

    // Format subjects for API - convert from component format (period_1_days) to API format (period_{periodId}_days)
    // Component uses period_1_days, period_2_days, etc. (based on position)
    // API expects period_{period_id}_days format where period_id is the actual period ID
    
    // Ensure we have periods - if not, this is a problem
    if (!periods || periods.length === 0) {
      setError('Periods not loaded. Please refresh the page and try again.');
      setSaving(false);
      return;
    }
    
    // Debug: Log the current formData.subjects to see what we're working with
    console.log('formData.subjects before conversion:', formData.subjects.map(s => ({
      name: s.subject_name || s.name,
      id: s.id,
      subject_id: s.subject_id,
      period_1_days: s.period_1_days,
      period_2_days: s.period_2_days,
      allPeriodKeys: Object.keys(s).filter(k => k.startsWith('period_'))
    })));
    
    const formattedSubjectLines = formData.subjects.map(subject => {
      const formatted = {};
      
      // Include ID if it exists (for updates)
      if (subject.id) {
        formatted.id = subject.id;
      }
      
      // Subject ID is required
      formatted.subject_id = subject.subject_id || subject.id;
      
      // Convert from component format (period_{periodNum}_days) to API format (period_{periodId}_days)
      // Sort periods to ensure consistent mapping
      // `periods` is already ordered by shift and then period number.
      const sortedPeriods = [...periods];
      
      console.log(`Converting subject ${subject.subject_name || subject.subject_id}:`, {
        sortedPeriods: sortedPeriods.map(p => ({ id: p.id, name: p.name })),
        subjectKeys: Object.keys(subject).filter(k => k.startsWith('period_'))
      });
      
      sortedPeriods.forEach((period, periodIdx) => {
        const periodId = period.id;
        const periodNum = periodIdx + 1; // Component uses 1-based index
        const componentKey = `period_${periodNum}_days`; // Component format
        
        // Backend expects period numbers (period_1_days, period_2_days) not period IDs (period_123_days)
        // This matches the backend's implementation which uses period index, not period database ID
        const apiKey = `period_${periodNum}_days`; // Use period number (1, 2, 3...) not period ID
        
        // Get data from component format
        const dayIds = subject[componentKey] || [];
        formatted[apiKey] = dayIds;
        
        console.log(`  Period ${periodNum} (ID: ${periodId}): ${componentKey} -> ${apiKey} = [${dayIds.join(', ')}]`);
      });
      
      return formatted;
    });

    try {
      setSaving(true);
      setError(null);
      
      // Validate timetable ID before API call
      if (!id || id === 'undefined') {
        setError('Timetable ID is required');
        setSaving(false);
        return;
      }
      
      const timetableId = parseInt(id);
      if (isNaN(timetableId) || timetableId <= 0) {
        setError('Invalid timetable ID format');
        setSaving(false);
        return;
      }
      
      const token = getToken();
      // Note: academic_year_id is not used by the API, so we don't include it
      // According to FRONTEND_DEVELOPER_GUIDE.md:
      // - Line 1141: "The model field is shift_ids (plural, Many2many), NOT shift_id (singular)"
      // - Line 1342-1345: Always use shift_ids: [1] (plural, array) - even for single shift
      if (!formData.shift_ids || formData.shift_ids.length === 0) {
        setError('Please select at least one shift');
        setSaving(false);
        return;
      }

      // Format exception dates if they exist
      const formattedExceptionDates = (formData.exception_dates || []).map(exception => {
        const formatted = {};
        if (exception.id) {
          formatted.id = exception.id; // Include ID for updates
        }
        formatted.start_date = exception.start_date;
        formatted.end_date = exception.end_date;
        if (exception.reason) {
          formatted.reason = exception.reason;
        }
        return formatted;
      });

      // Apply timetable changes from today. The backend preserves finalized
      // marked/cancelled sessions and rebuilds only unmarked sessions.
      const now = new Date();
      const localToday = new Date(now.getTime() - (now.getTimezoneOffset() * 60000));
      const effectiveFromDate = localToday.toISOString().split('T')[0];
      
      const updatePayload = {
        faculty_id: formData.faculty_id || undefined,
        campus: formData.campus,
        batch_ids: formData.batch_ids,
        semester: formData.semester,
        // Send as shift_ids array (as guide specifies) - Many2many field requires array format
        // For single shift: [1], for multiple shifts: [1, 2]
        shift_ids: formData.shift_ids, // Already an array from form state
        start_date: formData.start_date,
        end_date: formData.end_date,
        subject_lines: formattedSubjectLines, // Use subject_lines as per API documentation
        exception_dates: formattedExceptionDates,
        // Preserve finalized attendance while synchronizing today's pending
        // sessions and all future sessions with the updated timetable.
        // Backend should:
        // 1. Keep all attendance records for dates < today unchanged
        // 2. Keep today's marked/cancelled records unchanged
        // 3. Rebuild today's unmarked and all future records from the schedule
        preserve_existing_attendance: true,
        update_future_only: true,
        effective_from_date: effectiveFromDate,
        // CRITICAL: Regenerate attendance records for future dates
        // This ensures that attendance section (Teacher Portal and Faculty Admin) reflects the updated schedule
        regenerate_attendance: true, // Backend should regenerate attendance records for future dates
        sync_attendance: true // Backend should sync attendance records with the new timetable schedule
      };
      
      console.log('Update payload with attendance preservation and regeneration flags:', {
        preserve_existing_attendance: true,
        update_future_only: true,
        effective_from_date: effectiveFromDate,
        regenerate_attendance: true,
        sync_attendance: true,
        note: 'Backend should: 1) Preserve attendance for dates before effective_from_date, 2) Regenerate attendance records for future dates based on new schedule'
      });

      // Debug logging
      console.log('Update payload:', JSON.stringify(updatePayload, null, 2));
      console.log('Formatted subject lines:', formattedSubjectLines);
      console.log('Periods used for conversion:', periods);

      const result = await attendanceAPI.updateTimetable(timetableId, updatePayload, token);

      console.log('Update API response (full):', JSON.stringify(result, null, 2));
      console.log('Update API response keys:', Object.keys(result || {}));
      console.log('Update API response.success:', result?.success);
      console.log('Update API response.data:', result?.data);
      console.log('Update API response.message:', result?.message);
      console.log('Update API response.error:', result?.error);

      if (result.success) {
        console.log('Update successful, response data:', result.data);
        console.log('Update successful, response message:', result.message);
        console.log('Full result object:', result);
        
        // Verify the update was actually applied by comparing sent data with response
        const responseTimetable = result.data || result.timetable;
        if (responseTimetable && responseTimetable.subjects) {
          // Check if at least one subject's schedule was updated
          let updateDetected = false;
          for (const sentSubject of formattedSubjectLines) {
            const responseSubject = responseTimetable.subjects.find(
              s => s.id === sentSubject.id || s.subject_id === sentSubject.subject_id
            );
            
            if (responseSubject && responseSubject.schedule) {
              // Check if period_1_days or period_2_days were sent
              const sentPeriod1Days = sentSubject.period_1_days || [];
              const sentPeriod2Days = sentSubject.period_2_days || [];
              
              if (sentPeriod1Days.length > 0 || sentPeriod2Days.length > 0) {
                // Check if response matches what we sent
                const responsePeriod1Ids = responseSubject.schedule.period_1_ids || [];
                const responsePeriod2Ids = responseSubject.schedule.period_2_ids || [];
                
                // Compare sent data with response
                const period1Match = JSON.stringify(sentPeriod1Days.sort()) === JSON.stringify(responsePeriod1Ids.sort());
                const period2Match = JSON.stringify(sentPeriod2Days.sort()) === JSON.stringify(responsePeriod2Ids.sort());
                
                if (!period1Match || !period2Match) {
                  console.error('⚠️ UPDATE VERIFICATION FAILED:', {
                    subject_id: sentSubject.subject_id,
                    subject_name: responseSubject.subject_name,
                    sent: { 
                      period_1_days: sentPeriod1Days, 
                      period_2_days: sentPeriod2Days,
                      full_sent_subject: sentSubject
                    },
                    received: { 
                      period_1_ids: responsePeriod1Ids, 
                      period_2_ids: responsePeriod2Ids,
                      full_schedule: responseSubject.schedule
                    },
                    match_details: {
                      period1_match: period1Match,
                      period2_match: period2Match,
                      period1_sent_sorted: sentPeriod1Days.sort(),
                      period1_received_sorted: responsePeriod1Ids.sort(),
                      period2_sent_sorted: sentPeriod2Days.sort(),
                      period2_received_sorted: responsePeriod2Ids.sort()
                    }
                  });
                  
                  // Show more detailed error with what was sent vs received
                  const errorDetails = `Sent: period_1_days=[${sentPeriod1Days.join(', ')}], period_2_days=[${sentPeriod2Days.join(', ')}]. Received: period_1_ids=[${responsePeriod1Ids.join(', ')}], period_2_ids=[${responsePeriod2Ids.join(', ')}]`;
                  console.error('Full error details:', errorDetails);
                  
                  setError(`⚠️ Update reported as successful, but the schedule for "${responseSubject.subject_name || 'subject'}" was not actually updated.\n\nSent: period_1_days=[${sentPeriod1Days.join(', ')}], period_2_days=[${sentPeriod2Days.join(', ')}]\nReceived: period_1_ids=[${responsePeriod1Ids.join(', ')}], period_2_ids=[${responsePeriod2Ids.join(', ')}]\n\nThis is a backend issue. Please check:\n1) Backend server was restarted after the fix\n2) Check browser console to see which endpoint was used (PUT /api/timetable/<id> or fallback)\n3) Backend logs for errors\n4) Verify the backend fix was applied to the correct endpoint`);
                  setSaving(false);
                  return;
                } else {
                  updateDetected = true;
                }
              }
            }
          }
          
          if (!updateDetected && formattedSubjectLines.some(s => (s.period_1_days && s.period_1_days.length > 0) || (s.period_2_days && s.period_2_days.length > 0))) {
            console.warn('⚠️ Backend returned success but no schedule updates detected in response.');
            setError('⚠️ Update reported as successful, but no schedule changes were detected. The backend may not be processing the period_1_days and period_2_days fields. Please contact the backend team.');
            setSaving(false);
            return;
          }
        } else if (!result.data && !result.timetable) {
          console.warn('⚠️ Backend returned success but no data in response. Update may not have been applied.');
          setError('⚠️ Backend returned success but no updated data. The update may not have been applied. Please check the backend logs.');
          setSaving(false);
          return;
        }
        
        // Log a reminder about attendance regeneration
        console.log('✅ Timetable updated successfully');
        console.log('⚠️ IMPORTANT: Backend should regenerate attendance records for future dates');
        console.log('⚠️ If attendance section does not reflect changes, backend may not be regenerating attendance records');
        console.log('📋 BACKEND DEBUGGING CHECKLIST:');
        console.log('   1. Check if backend is reading preserve_existing_attendance from params');
        console.log('   2. Check if backend is reading regenerate_attendance from params');
        console.log('   3. Check if backend is reading effective_from_date from params');
        console.log('   4. Check backend logs to see if _regenerate_attendance_sessions() is being called');
        console.log('   5. Check if backend is processing period_1_days and period_2_days correctly');
        console.log('   6. For JSON-RPC 2.0 format, backend should read from: request.jsonrequest.get("params")');
        
        setSuccess(true);
        setTimeout(() => {
          navigate(`/attendance/faculty-admin/timetable/${id}`);
        }, 1500);
      } else {
        // Check both error and message fields from backend response
        const errorMessage = result.error || result.message || 'Failed to update timetable';
        console.error('Update failed:', errorMessage);
        setError(errorMessage);
      }
    } catch (err) {
      console.error('Error updating timetable:', err);
      setError(err.message || 'Failed to update timetable');
    } finally {
      setSaving(false);
    }
  };

  const toggleBatch = (batchId) => {
    setFormData(prev => ({
      ...prev,
      // Each timetable belongs to exactly one batch.
      batch_ids: prev.batch_ids.includes(batchId) ? [] : [batchId]
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading timetable...</p>
        </div>
      </div>
    );
  }

  if (timetableState && timetableState === 'ended') {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
        <h3 className="text-yellow-800 font-semibold mb-2">Cannot Edit Timetable</h3>
        <p className="text-yellow-700 mb-4">
          Cannot edit ended timetables. This timetable is currently in "{timetableState}" state.
        </p>
        <button
          onClick={() => navigate(`/attendance/faculty-admin/timetable/${id}`)}
          className="px-4 py-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600 transition"
        >
          View Timetable
        </button>
      </div>
    );
  }

  const getErrorText = (value) => {
    if (!value) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    if (value.message) return value.message;
    if (value.data?.message) return value.data.message;
    if (value.code || value.data || value.message) {
      try {
        return JSON.stringify(value);
      } catch (err) {
        return 'An unexpected error occurred.';
      }
    }
    return 'An unexpected error occurred.';
  };

  const errorText = getErrorText(error);
  const selectedBatchId = formData.batch_ids[0] || '';
  const selectedBatch = batches.find(batch => String(batch.id) === String(selectedBatchId));
  const selectedDepartmentId = formData.department_id || selectedBatch?.department_id || '';
  const departmentName = formData.department_name || selectedBatch?.department || '';
  const departmentBatches = selectedDepartmentId
    ? batches.filter(batch => String(batch.department_id) === String(selectedDepartmentId))
    : batches;
  const setupReady = Boolean(selectedBatchId && formData.semester && formData.shift_ids.length);
  const selectClassName = 'w-full h-11 px-3 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed';

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate(`/attendance/faculty-admin/timetable/${id}`)}
          className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 mb-4 transition"
        >
          <MdArrowBack />
          <span>Back to Timetable</span>
        </button>

        <h1 className="text-2xl font-bold text-gray-800">Edit Timetable</h1>
        <p className="text-gray-600 text-sm mt-1">Update timetable information</p>
      </div>

      {/* Success Message */}
      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-green-800 font-semibold">
            {'Timetable updated successfully! Redirecting...'}
          </p>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800">{errorText}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
        <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="flex flex-col gap-1 border-b border-gray-100 bg-gray-50 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-base font-semibold text-gray-900">Timetable Setup</h2>
              <p className="mt-0.5 text-sm text-gray-500">Review or update the existing timetable filters.</p>
            </div>
            <p className="text-sm text-gray-500">Current timetable</p>
          </div>

          <div className="grid grid-cols-1 gap-4 p-5 sm:grid-cols-2 xl:grid-cols-4">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-gray-700">Department <span className="text-red-500">*</span></label>
              <select
                value={selectedDepartmentId}
                onChange={(e) => {
                  const department = departments.find(item => String(item.id) === e.target.value);
                  setFormData(prev => ({
                    ...prev,
                    department_id: e.target.value,
                    department_name: department?.name || '',
                    batch_ids: [],
                    semester: '',
                    subjects: []
                  }));
                }}
                className={selectClassName}
                disabled={timetableState === 'active'}
                required
              >
                <option value="">Select Department</option>
                {departments.map(department => (
                  <option key={department.id} value={department.id}>{department.name || department.code}</option>
                ))}
                {selectedDepartmentId && !departments.some(item => String(item.id) === String(selectedDepartmentId)) && (
                  <option value={selectedDepartmentId}>{departmentName || 'Current Department'}</option>
                )}
              </select>
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium text-gray-700">Campus <span className="text-red-500">*</span></label>
              <select
                value={formData.campus}
                onChange={(e) => {
                  autoPopulateKeyRef.current = '';
                  setFormData(prev => ({ ...prev, campus: e.target.value }));
                }}
                className={selectClassName}
                disabled={Boolean(timetableState && formData.campus)}
                required
              >
                <option value="">Campus not set</option>
                {campuses.map(campus => <option key={campus.id} value={campus.id}>{campus.name}</option>)}
              </select>
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium text-gray-700">Batch <span className="text-red-500">*</span></label>
              <select
                value={selectedBatchId}
                onChange={(e) => {
                  const batchId = parseInt(e.target.value);
                  if (!Number.isNaN(batchId)) toggleBatch(batchId);
                }}
                className={selectClassName}
                disabled={timetableState === 'active'}
                required
              >
                <option value="">Select Batch</option>
                {departmentBatches.map(batch => (
                  <option key={batch.id} value={batch.id}>{batch.name || batch.code}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium text-gray-700">Semester <span className="text-red-500">*</span></label>
              <select
                value={formData.semester}
                onChange={(e) => setFormData(prev => ({ ...prev, semester: e.target.value }))}
                disabled={!selectedBatchId}
                className={selectClassName}
                required
              >
                <option value="">{selectedBatchId ? 'Select Semester' : 'Select batch first'}</option>
                {Array.from({ length: 12 }, (_, i) => i + 1).map(sem => (
                  <option key={sem} value={sem}>Semester {sem}</option>
                ))}
              </select>
            </div>
          </div>

          {formData.semester && (
            <div className="border-t border-gray-100 px-5 py-4">
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-[1fr_1fr_2fr]">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Start date</p>
                  <p className="mt-1 font-semibold text-gray-900">{formData.start_date || 'Not configured'}</p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-500">End date</p>
                  <p className="mt-1 font-semibold text-gray-900">{formData.end_date || 'Not configured'}</p>
                </div>
                <fieldset>
                  <legend className="text-xs font-medium uppercase tracking-wide text-gray-500">Shift <span className="text-red-500">*</span></legend>
                  <div className="mt-2 flex flex-wrap gap-x-6 gap-y-2">
                    {shifts.map(shift => (
                      <label key={shift.id} className="flex items-center gap-2 text-sm font-medium text-gray-800">
                        <input
                          type="checkbox"
                          checked={formData.shift_ids.includes(shift.id)}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            shift_ids: e.target.checked
                              ? [...prev.shift_ids, shift.id]
                              : prev.shift_ids.filter(shiftId => shiftId !== shift.id)
                          }))}
                          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        {shift.name || shift.code}
                      </label>
                    ))}
                  </div>
                </fieldset>
              </div>
              {activeTermConfig && (
                <p className="mt-4 text-xs text-gray-500">Dates are managed by {activeTermConfig.display_name}.</p>
              )}
            </div>
          )}
        </section>

        {/* Subjects & Schedule Matrix */}
        {setupReady && (
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-800">Subjects & Schedule Matrix</h2>
              <p className="text-sm text-gray-600 mt-1">
                Assign subjects to specific periods and days of the week
              </p>
            </div>
            <div className="flex items-center space-x-2">
              {formData.batch_ids.length > 0 && formData.semester && formData.shift_ids.length > 0 && (
                <>
                  <button
                    type="button"
                    onClick={() => setShowAddSubjectModal(true)}
                    disabled={loading}
                    className="px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2 disabled:opacity-50 text-sm font-semibold"
                  >
                    <MdAdd />
                    <span>Add Subject Manually</span>
                  </button>
                </>
              )}
            </div>
          </div>

          {formData.subjects.length > 0 ? (
            <div className="space-y-4">
              {/* Subject List with Delete Option */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Subjects ({formData.subjects.length})</h3>
                <div className="flex flex-wrap gap-2">
                  {formData.subjects.map((subject, index) => (
                    <div
                      key={subject.id || subject.subject_id || index}
                      className="flex items-center space-x-2 bg-white border border-gray-300 rounded-md px-3 py-2"
                    >
                      <span className="text-sm font-medium text-gray-800">
                        {subject.name || subject.subject_name}
                      </span>
                      {subject.is_shared && <SharedClassBadge />}
                      <button
                        type="button"
                        onClick={() => {
                          setFormData(prev => ({
                            ...prev,
                            subjects: prev.subjects.filter((_, i) => i !== index)
                          }));
                        }}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 rounded p-1 transition"
                        title="Remove subject"
                      >
                        <MdDelete className="text-sm" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Schedule Matrix */}
              <SubjectScheduleMatrix
                subjects={formData.subjects}
                periods={periods}
                weekdays={weekdays}
                onSubjectsChange={(updatedSubjects) => {
                  setFormData(prev => ({
                    ...prev,
                    subjects: updatedSubjects
                  }));
                }}
                selectedShifts={shifts.filter(s => formData.shift_ids.includes(s.id))}
              />
            </div>
          ) : (
            <div className="py-8 text-center text-sm text-gray-500">
              <p>No assigned subjects were found for this batch and semester.</p>
            </div>
          )}
        </div>
        )}

        {/* Submit Button */}
        <div className="flex flex-col sm:flex-row justify-end gap-3 sm:gap-4 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={() => navigate(`/attendance/faculty-admin/timetable/${id}`)}
            className="w-full sm:w-auto px-6 py-2.5 sm:py-3 border-2 border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition font-medium text-sm sm:text-base"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="w-full sm:w-auto px-6 py-2.5 sm:py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 text-sm sm:text-base shadow-md hover:shadow-lg"
          >
            <MdSave />
            <span>{saving ? 'Saving...' : 'Save Changes'}</span>
          </button>
        </div>
      </form>

      {/* Add Subject Manually Modal */}
      <AddSubjectModal
        isOpen={showAddSubjectModal}
        onClose={() => setShowAddSubjectModal(false)}
        onAdd={(newSubjects) => {
          // Handle both single subject (backward compatibility) and array of subjects
          const subjectsArray = Array.isArray(newSubjects) ? newSubjects : [newSubjects];
          
          // Initialize period days arrays for each new subject
          const subjectsWithSchedule = subjectsArray.map(newSubject => {
            const subjectWithSchedule = { ...newSubject };
            periods.forEach((_, periodIdx) => {
              const periodNum = periodIdx + 1;
              subjectWithSchedule[`period_${periodNum}_days`] = [];
            });
            return subjectWithSchedule;
          });
          
          setFormData(prev => ({
            ...prev,
            subjects: [...prev.subjects, ...subjectsWithSchedule]
          }));
        }}
        batchIds={formData.batch_ids}
        campus={formData.campus}
        facultyId={formData.faculty_id}
        semester={formData.semester}
        existingSubjectIds={formData.subjects.map(s => s.subject_id || s.id).filter(Boolean)}
      />
    </div>
  );
}

export default EditTimetable;
